import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Eye } from "lucide-react";
import VideoPlayer from "../components/VideoPlayer";
import { YoutubeWatchButton } from "../components/WatchActions";
import { getVideo, getYoutubeId } from "../lib/api";
import { getVideoSourceType } from "../lib/video";
import { usePageTitle } from "../hooks/usePageTitle";

export default function VideoDetail() {
  const { id } = useParams();
  const [video, setVideo] = useState(null);
  const [notFound, setNotFound] = useState(false);

  usePageTitle(video?.title);

  useEffect(() => {
    window.scrollTo(0, 0);
    setNotFound(false);
    getVideo(id)
      .then(setVideo)
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-[140px] pb-24 tv-container text-center">
        <p className="text-white text-xl">Video bulunamadı.</p>
        <Link to="/videolar" className="tv-btn-primary mt-6 inline-flex">Videolara Dön</Link>
      </div>
    );
  }

  if (!video) return <div className="pt-[200px] pb-40" />;

  const sourceType = getVideoSourceType(video);
  const hasYoutube = Boolean(getYoutubeId(video.youtubeUrl));
  const backToProgram = video.programId
    ? { to: `/programlar/${video.programId}`, label: `${video.programName || "Program"} sayfasına dön` }
    : null;

  return (
    <div className="pt-[74px]">
      <div className="tv-container py-8 md:py-10">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <Link
            to="/videolar"
            className="inline-flex items-center gap-2 text-sm text-[var(--tv-muted)] hover:text-white transition-colors"
          >
            <ArrowLeft size={16} /> Tüm Videolar
          </Link>
          {backToProgram && (
            <Link
              to={backToProgram.to}
              className="inline-flex items-center gap-2 text-sm text-[var(--tv-muted)] hover:text-white transition-colors"
            >
              <ArrowLeft size={16} /> {backToProgram.label}
            </Link>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="tv-card !overflow-hidden"
        >
          <div className="relative aspect-video bg-black">
            <VideoPlayer video={video} autoplay />
          </div>

          <div className="p-5 md:p-6">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              {video.programName && <span className="tv-badge">{video.programName}</span>}
              {sourceType === "upload" && <span className="tv-badge">Yüklenen video</span>}
              {sourceType === "youtube" && <span className="tv-badge">YouTube</span>}
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-white font-display">{video.title}</h1>
            {video.description && (
              <p className="text-[var(--tv-muted)] mt-3 leading-relaxed">{video.description}</p>
            )}

            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-[var(--tv-muted)]">
              {video.publishedAt && <span>{video.publishedAt}</span>}
              {video.views && (
                <span className="flex items-center gap-1.5">
                  <Eye size={14} /> {video.views}
                </span>
              )}
              {video.duration && <span>{video.duration}</span>}
            </div>

            {hasYoutube && (
              <div className="mt-6 pt-5 border-t border-[var(--tv-border)]">
                <p className="text-sm text-[var(--tv-muted)] mb-3">Alternatif izleme</p>
                <YoutubeWatchButton url={video.youtubeUrl} />
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
